<script>
import { mapGetters } from 'vuex'

import WriteReview from '@components/reviews/write-review.vue'
import InfoSection from '@components/reviews/info-section.vue'
import ReviewsSection from '@components/reviews/reviews-section.vue'
import StarsRating from '@components/stars-rating.vue'
import Slider from '@components/common/sliders/attractions-slider.vue'

import Layout from '@layouts/main.vue'

import Translatable from '@src/mixins/translatable.js'
import SanitizerMixin from '@src/mixins/sanitizer-mixin.js'
import ImageMixin from '@src/mixins/imageMixin.js'
import Resizable from '@src/mixins/resizable.js'
import AuthorLabel from '@src/mixins/author.js'

export default {
  page: {
    title: 'Reviews',
    meta: [{ name: 'description', content: 'Reviews' }],
  },
  mixins: [
    Translatable,
    SanitizerMixin,
    ImageMixin,
    Resizable,
    Slider,
    AuthorLabel
  ],
  components: {
    Layout,
    ReviewsSection,
    WriteReview,
    InfoSection,
    StarsRating
  },
  computed: {
    ...mapGetters('reviewsPage', [
      'pageData',
    ]),
    ...mapGetters('reviews', [
      'averageRating',
      'reviewsCards'
    ]),
    pageImg() {
      if (this.pageData.mainImage) {
        return this.desktopScreen ? this.pageData.mainImage.web_image : this.pageData.mainImage.app_image
      }
      return ''
    },
  },
  methods: {
    writeBtnClck() {
      this.scrollToElement({behavior: 'smooth'})
    },
    scrollToElement(options) {
      const reviewsList = document.querySelector('.write-review')

      if (reviewsList) {
        reviewsList.scrollIntoView(options);
      }
    },
  },
}
</script>

<template>
  <Layout>
    <section class="header justify-center" :style="getBackground(pageImg)">
      <div class="header-block">
        <h1 class="header-title" v-html="getTranslatedContent('title', pageData.content)"></h1>
        <h3 class="header-subtitle" v-html="getTranslatedContent('subtitle', pageData.content)"></h3>
      </div>
    </section>
    <section class="description-banner justify-center">
      <p class="author container" v-html="authorLabel(pageData)"></p>
      <div class="description">
        <div class="rating-wrapper">
          <div class="average-review-rating">
            <div class="average-rating">
              {{ averageRating && averageRating.toFixed(1) }}
            </div>
            <StarsRating :rating="averageRating && averageRating.toFixed(0)"/>
          </div>
          <p class="rating-text">{{ getTranslatedContent('REVIEWS_rating_label').replace( "$count", reviewsCards.length) }}</p>
        </div>
        <div class="text-container">
          <p class="description-text" v-html="bannerPageText(pageData.content)"></p>
        </div>
        <div class="button-container" @click="writeBtnClck">
          <button class="button-send">{{ getTranslatedContent('REVIEWS_write_your_opinion') }}</button>
        </div>
      </div>
    </section>
    <section class="reviews justify-center">
      <div class="container">
        <ReviewsSection />
      </div>
    </section>
    <section id="write-review" class="write-review justify-center">
      <div class="container">
        <WriteReview />
      </div>
    </section>
    <section class="info justify-center">
      <div class="container">
        <InfoSection />
      </div>
    </section>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
@import '@styles/author_text.scss';

.header {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  height: 550px;
  width: 100%;
}

.header-block {
  width: 840px;
  height: 135px;
  // background: rgba(37, 44, 62, 0.36);
  padding-top: 9px;
  box-sizing: border-box;
  margin-top: 217px;
  margin-right: 300px;

  .header-title {
    font-family: Open Sans Bold, sans-serif;
    font-weight: bold;
    font-size: 48px;
    line-height: 62px;
    text-transform: uppercase;
    margin-left: 22px;
    color: $white;
  }

  .header-subtitle {
    font-family: Open Sans Semibold, sans-serif;
    font-weight: 600;
    font-size: 22px;
    line-height: 29px;
    margin-left: 25px;
    color: $white;
  }
}

::v-deep .description-banner {
  margin: -99px 0 40px;
  position: relative;
  & .description {
    width: 1140px;
    min-height: 100px;
    height: auto;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    border-radius: 20px;
    box-sizing: border-box;
    background: $white;
    box-shadow: 0 8px 12px rgba(76, 72, 124, 0.08);
    padding: 25px 40px 25px 50px;

    .text-container {
      width: 459px;

      & .description-text {
        user-select: none;

        & em {
          font-style: italic;
        }
        & a {
          color: $gray;
          text-decoration: none;

          &:hover {
            color: $orange;
          }
        }
        & ul {
          margin-left: 20px;
          list-style: initial;
        }

        & strong {
          font-family: Open Sans Bold, sans-serif;
          font-style: normal;
          font-weight: bold;
        }

        & .ql-align-center {
          text-align: center;
        }
      }
    }

    p {
      font-family: Open Sans, sans-serif;
      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 21px;
      color: $dark-blue;
    }

    .rating-text {
      font-size: 14px;
      line-height: 23px;
    }
  }
}

.button-container {
  min-width: 221px;
  align-self: center;
}

.button-send {
  width: 100%;
  height: 48px;
  border: 1px solid $dark-blue;
  border-radius: 5px;
  background: $dark-blue;
  color: $white;
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-style: normal;
  text-transform: uppercase;
  font-size: 15px;
  line-height: 130%;
  padding: 15px 30px;

  &:hover {
    border: 1px solid $hover-blue;
    background: $hover-blue;
  }
}

.rating-wrapper {
  display: flex;
  flex-direction: column;
}

.average-review-rating {
  display: flex;
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 22px;
  line-height: 29px;
  color: $dark-blue;
  margin-bottom: 10px;
}

.average-rating {
  margin-right: 12px;
}

@media only screen and (max-width: $screen-width-xs) {

  .container {
    width: 100%;
  }

  .header {
    height: 244px;
  }

  .header-block {
    width: 100%;
    margin-top: 70px;
    margin-right: 0;
    padding-top: 0;
    height: 125px;

    .header-title {
      font-family: Open Sans Bold, sans-serif;
      font-weight: bold;
      font-size: 26px;
      line-height: 34px;
      text-transform: uppercase;
      margin: 0px 8px 10px;
      color: $white;
    }

    .header-subtitle {
      font-family: Open Sans Semibold, sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 21px;
      color: $white;
      margin: 0 8px;
    }
  }

  .description-banner {
    margin: -20px 0 20px;

    & .description {
      flex-direction: column;
      width: 100%;
      min-height: 240px;
      height: auto;
      padding: 20px 8px;

      .text-container {
        width: 100%;
        margin: 17px 0 25px;
      }

      p {
        font-size: 14px;
        line-height: 23px;
      }

      .link {
        width: 100%;
      }
    }
  }

  .button-container {
    width: 100%;
  }

  .areas-wrapper {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    column-gap: 20px;
    margin-top: 30px;
  }
}
</style>
